import { useCallback, useEffect, useMemo, useState } from 'react';
import backendApi from '../services/backendApi';

export interface ModuleAccessItem {
    module_key: string;
    module_name?: string;
    route?: string;
    visible?: boolean;
    actions?: Record<string, boolean>;
}

interface ModuleAccessState {
    loading: boolean;
    loadedFromApi: boolean;
    modules: ModuleAccessItem[];
    allowedModuleKeys: Set<string>;
}

export const useModuleAccess = (siteCode?: string) => {
    const [state, setState] = useState<ModuleAccessState>({
        loading: true,
        loadedFromApi: false,
        modules: [],
        allowedModuleKeys: new Set<string>(),
    });

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            const hasAccessToken = !!sessionStorage.getItem('access_token');
            if (!hasAccessToken) {
                if (mounted) {
                    setState({
                        loading: false,
                        loadedFromApi: false,
                        modules: [],
                        allowedModuleKeys: new Set<string>(),
                    });
                }
                return;
            }

            const response = await backendApi.getNavigationModules(siteCode || null);

            if (!mounted) {
                return;
            }

            if (response.success && Array.isArray(response.modules)) {
                const allowed = new Set<string>(
                    response.modules
                        .filter((module: ModuleAccessItem) => module.visible)
                        .map((module: ModuleAccessItem) => module.module_key)
                );

                setState({
                    loading: false,
                    loadedFromApi: true,
                    modules: response.modules,
                    allowedModuleKeys: allowed,
                });
                return;
            }

            // Backward-compatible fallback: when API is unavailable,
            // keep existing navigation behavior.
            setState({
                loading: false,
                loadedFromApi: false,
                modules: [],
                allowedModuleKeys: new Set<string>(),
            });
        };

        load();

        return () => {
            mounted = false;
        };
    }, [siteCode]);

    const hasModuleAccess = useCallback((moduleKey: string) => {
        if (!moduleKey) {
            return false;
        }
        // While loading, treat everything as locked — prevents flash of unlocked modules
        if (state.loading) {
            return false;
        }
        // Strict mode: when API resolved successfully, empty allowed list means no access.
        if (state.loadedFromApi) {
            return state.allowedModuleKeys.has(moduleKey);
        }
        // Backward compatibility only for API-unavailable cases.
        if (state.modules.length === 0) {
            return true;
        }
        return state.allowedModuleKeys.has(moduleKey);
    }, [state.loading, state.loadedFromApi, state.modules.length, state.allowedModuleKeys]);

    return useMemo(() => ({
        loading: state.loading,
        loadedFromApi: state.loadedFromApi,
        modules: state.modules,
        allowedModuleKeys: state.allowedModuleKeys,
        hasModuleAccess,
    }), [state.loading, state.loadedFromApi, state.modules, state.allowedModuleKeys, hasModuleAccess]);
};

export default useModuleAccess;
